import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useTaxonomyContext } from "../../../../../contexts/Taxonomy/TaxonomyContext";
import {
  CompanyFilter,
  CompanyMatchFilter,
} from "../../../../../legacy/types/companyFilter";
import { SEGMENT_ANALYSIS_INDUSTRY_BREAKDOWN } from "../../../../../legacy/queries/MI";
import { SpendBreakdownResult } from "../../../../../legacy/types/companySearch/MI";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
} from "../../../../../generated/company-search";

interface IndustryBreakdownQueryPayload {
  segmentAnalysisIndustryBreakdown: IndustrySpendBreakdown[];
}

interface IndustrySpendBreakdown {
  companyCount: number;
  totalSpend: number;
  industryId: number;
}

interface IndustryBreakdownQueryVariables {
  companyFilters?: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  localizeInstalls?: boolean;
  companyMatchFile?: CompanyMatchFilter;
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
}

interface Props {
  companyFilters: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  localizeInstalls?: boolean;
  /** V2 ("model tier") scoring narrowing — mirrors opportunityGeneratorSearch's
   * args. */
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  /** Share-page panel tag (Apollo context); see toPanelDescriptor. */
  activeModelId?: string;
  /** Skips the query entirely. Omitted (undefined) behaves exactly like
   * `false` — existing callers that never pass this see no change. */
  skip?: boolean;
}

export function useGetSpendByHGIndustryData(props: Props) {
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );
  const { hgIndustries } = useTaxonomyContext();

  const industriesMap = useMemo(() => {
    const map = {} as Record<number, string>;

    for (const industry of hgIndustries || []) {
      if (typeof industry.id === "number") {
        map[industry.id] = industry.name || "";
      }
    }

    return map;
  }, [hgIndustries]);

  const { data, loading, error } = useQuery<
    IndustryBreakdownQueryPayload,
    IndustryBreakdownQueryVariables
  >(SEGMENT_ANALYSIS_INDUSTRY_BREAKDOWN, {
    variables: {
      companyFilters: props.companyFilters,
      customSpendCategory: props.customSpendCategory,
      localizeInstalls: props.localizeInstalls,
      companyMatchFile:
        props.companyFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
      tenantId: props.tenantId,
      modelScores: props.modelScores,
      modelScoreFilter: props.modelScoreFilter,
    },
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
      activeModelId: props.activeModelId,
    },
    skip: props.skip,
  });

  const spendBreakDownResult = useMemo<SpendBreakdownResult[]>(() => {
    return (
      // industry.id value of 0 does not exist in industriesQueryResults
      data?.segmentAnalysisIndustryBreakdown.map(
        ({ companyCount, totalSpend, industryId }) => ({
          id: industryId,
          spend: totalSpend,
          companies: companyCount,
          dimension: industriesMap[industryId] || "Unknown",
        })
      ) ?? []
    );
  }, [data?.segmentAnalysisIndustryBreakdown, industriesMap]);

  return {
    spendByHGIndustryChartData: spendBreakDownResult,
    loading,
    error,
  };
}
